/**
 * RegisterChoiceScreen - Choose how to register: manual form or share with AI agent
 */

import { useCallback } from 'react';
import { hapticFeedback } from '../lib/telegram';
import { API_BASE_URL } from '../lib/api';
import { showToast } from '../components/Toast';
import { postEvent } from '@telegram-apps/sdk-react';

interface RegisterChoiceScreenProps {
  onManual: () => void;
  onBack: () => void;
}

const REGISTER_MESSAGE = `🦞 TeleClaw — Register on the AI Agent Directory

I want to list one of our bots/mini apps on TeleClaw (the AI Agent Directory on Telegram).

Please check what bots or mini apps you've built or manage for me, and list them out so I can choose which one to register.

Once I pick one, register it using this API:

POST ${API_BASE_URL}/api/register-request
Content-Type: application/json
Body: { "bot_username": "(without @)", "name": "Agent Name", "description": "What it does", "category": "social|trading|infrastructure|defi|nft|gaming|tools|analytics|education|entertainment|utility|ai|finance|productivity|communication|other", "website": "url (optional)", "tags": ["tag1", "tag2"], "logo_url": "url to bot profile image (optional)", "has_mini_app": true/false }

IMPORTANT: Please include logo_url (the bot's profile picture URL or any relevant image) and set has_mini_app to true if this bot has a Telegram Mini App set up via BotFather /newapp.

You'll get back a confirmation_link — send it to me and I'll tap to confirm the listing!

If another app referred you, include their referral_code in your request for a mutual boost!`;

export function RegisterChoiceScreen({ onManual, onBack: _onBack }: RegisterChoiceScreenProps) {
  const handleShareWithAgent = useCallback(() => {
    hapticFeedback('medium');
    
    // Use Telegram's share URL scheme to let user pick a chat
    const encoded = encodeURIComponent(REGISTER_MESSAGE);
    try {
      postEvent('web_app_open_tg_link', { path_full: `/share/url?url=&text=${encoded}` });
    } catch {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(REGISTER_MESSAGE).then(() => {
        showToast('Registration instructions copied! Paste them to your AI agent chat.', 'success');
      });
    }
  }, []);

  const handleManual = useCallback(() => {
    hapticFeedback('light');
    onManual();
  }, [onManual]);

  return (
    <div className="max-w-md mx-auto pb-8">
      <div className="text-center mb-8 mt-4">
        <p className="text-5xl mb-4">🦞</p>
        <h2 className="text-2xl font-bold mb-2">List Your App</h2>
        <p className="text-[var(--tg-theme-hint-color)]">
          Choose how you'd like to register
        </p>
      </div>

      <div className="space-y-4">
        {/* Share with AI Agent */}
        <button
          onClick={handleShareWithAgent}
          className="w-full p-5 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-2xl text-left hover:border-blue-500/50 transition-all"
        >
          <div className="flex items-start gap-4">
            <span className="text-3xl">🤖</span>
            <div>
              <h3 className="text-lg font-semibold mb-1">Share with your AI Agent</h3>
              <p className="text-sm text-[var(--tg-theme-hint-color)]">
                Forward registration instructions to your bot. Your AI agent will handle the rest — you just confirm.
              </p>
              <p className="text-xs text-blue-400 mt-2">⚡ Recommended</p>
            </div>
          </div>
        </button>

        {/* Manual Form */}
        <button
          onClick={handleManual}
          className="w-full p-5 bg-[var(--tg-theme-secondary-bg-color)] rounded-2xl text-left hover:brightness-110 transition-colors"
        >
          <div className="flex items-start gap-4">
            <span className="text-3xl">📝</span>
            <div>
              <h3 className="text-lg font-semibold mb-1">Fill out manually</h3>
              <p className="text-sm text-[var(--tg-theme-hint-color)]">
                Enter your app's details yourself using the registration form.
              </p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
